import React, { useState, useEffect } from 'react';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { FormControlLabel, Checkbox, IconButton, Grid } from '@mui/material';
import { InputText } from 'primereact/inputtext';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { ToastsContainer, ToastsStore, ToastsContainerPosition } from 'react-toasts';
import { Modal, ModalHeader, ModalBody, ModalFooter, Row, Col } from 'reactstrap';
import { constants } from '../../constants/Constants';
import routes from '../../constants/Routes';
import CreateTwoToneIcon from '@mui/icons-material/CreateTwoTone';
import { getAllCampaigns } from '../../services/campaigns.service';
import { Campaign } from '@horasys/bellplus/common/types';
import AlertBox from 'views/Common/AlertBox';

const PREFIX = 'CampaignAudit';

const classes = {
	fab: `${PREFIX}-fab`,
	absolute: `${PREFIX}-absolute`,
	fabIcon: `${PREFIX}-fabIcon`,
	textEllipsisEffect: `${PREFIX}-textEllipsisEffect`,
};

const Root = styled('div')(({ theme }) => ({
	[`& .${classes.fab}`]: {
		margin: theme.spacing(2),
	},

	[`& .${classes.absolute}`]: {},

	[`& .${classes.fabIcon}`]: {
		color: theme.palette.common.white,
		backgroundColor: theme.palette.primary.main,
		'&:hover': {
			backgroundColor: theme.palette.primary.light,
		},
	},

	[`& .${classes.textEllipsisEffect}`]: {
		whiteSpace: 'nowrap',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
	},
}));

const CampaignAudit = () => {
	const [campaignList, setCampaignList] = useState<Array<Campaign>>([]);
	const [isOpenModalDelete, setOpenModalDelete] = useState(false);

	const [isFilterHidden, setIsFilterHidden] = useState(false);
	const [globalFilter, setGlobalFilter] = useState('');

	const [alertError, setAlertError] = useState({ message: '', open: false });

	useEffect(() => {
		const getDetails = async () => {
			try {
				const campaigns: Array<Campaign> = await getAllCampaigns();
				setCampaignList(campaigns);
			} catch (error) {
				const alert = {
					message: 'Unable to fetch campaign audit details',
					open: true,
				};
				setAlertError(alert);
			}
		};

		getDetails();
	}, []);

	const header = (
		<Root>
			<div>
				<div style={{ textAlign: 'right' }}>
					<FormControlLabel
						control={
							<Checkbox
								name="showFilters"
								color="primary"
								onChange={(e) => onFiltersShow(e.target.checked)}
							/>
						}
						label="Show Filters"
					/>
				</div>
			</div>
		</Root>
	);
	const navigate = useNavigate();
	const closeModalDelete = () => {
		setOpenModalDelete(false);
	};

	const openEditModal = (rowData: any) => {
		navigate(routes.VIEW_CAMPAIGN_AUDIT.path, { state: { message: rowData } });
	};

	const onFiltersShow = (isFilterShown: any) => {
		setIsFilterHidden(isFilterShown);
	};

	const actionTemplate = (rowData: any, column: any) => {
		return (
			<>
				<IconButton
					aria-label="edit"
					onClick={() => {
						openEditModal(rowData);
					}}
					size="large">
					<CreateTwoToneIcon fontSize="small" />
				</IconButton>
			</>
		);
	};

	return (
		<>
			<div>
				<AlertBox
					message={alertError.message}
					shouldOpen={alertError.open}
					type="warning"
				/>
				<ToastsContainer
					className="toast"
					store={ToastsStore}
					position={ToastsContainerPosition.TOP_CENTER}
				/>
				<Grid container justifyContent="space-between">
					<Grid item>
						<InputText
							type="search"
							placeholder="Global Search"
							onInput={(e) => setGlobalFilter(e.currentTarget.value)}
							style={{ width: '500px', margin: '10px' }}
						/>
					</Grid>
				</Grid>
				<div style={{ backgroundColor: '#ffffff', margin: '10px' }}>
					<div className="content-section implementation">
						<DataTable
							reorderableColumns={true}
							value={campaignList}
							globalFilter={globalFilter}
							emptyMessage="No records found"
							header={header}
							paginator={true}
							paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink
      LastPageLink CurrentPageReport RowsPerPageDropdown"
							currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
							rows={10}
							rowsPerPageOptions={[5, 10, 20]}
							selectionMode="single">
							<Column
								field="id"
								header="Id"
								sortable={true}
								filter={isFilterHidden}
								style={{ width: '3em' }}
								className={classes.textEllipsisEffect}
							/>
							<Column
								field="client.name"
								header="Client Name"
								sortable={true}
								filter={isFilterHidden}
								style={{ width: '12em' }}
								className={classes.textEllipsisEffect}
							/>
							<Column
								field="name"
								header="Campaign Name"
								sortable={true}
								filter={isFilterHidden}
								style={{ width: '15em' }}
								className={classes.textEllipsisEffect}
							/>
							<Column
								field="adCount"
								header="Ads Count"
								sortable={true}
								filter={isFilterHidden}
								style={{ width: '5em' }}
								className={classes.textEllipsisEffect}
							/>
							<Column
								field="status"
								header="Campaign Status"
								sortable={true}
								filter={isFilterHidden}
								className={classes.textEllipsisEffect}
							/>
							<Column
								field="price"
								header="Campaign Price"
								sortable={true}
								filter={isFilterHidden}
								className={classes.textEllipsisEffect}
							/>
							<Column
								field="paymentReceived"
								header="Payment received"
								sortable={true}
								filter={isFilterHidden}
								className={classes.textEllipsisEffect}
							/>
							<Column
								body={actionTemplate}
								style={{ textAlign: 'center', width: '5em' }}
								header="Actions"
								field=""
								reorderable={false}
								sortable={false}
							/>
						</DataTable>
					</div>
				</div>
			</div>

			<Modal
				isOpen={isOpenModalDelete}
				toggle={closeModalDelete}
				size="lg"
				autoFocus={true}>
				<ModalHeader toggle={closeModalDelete}>
					<span>Delete</span>
				</ModalHeader>
				<ModalBody>
					<Row>
						<Col xs="4"></Col>
						Are you sure you want to delete ?
					</Row>
				</ModalBody>
				<ModalFooter>
					<Col xs="2" md="2">
						<button className="btn-white" onClick={closeModalDelete}>
							{constants.NO}
						</button>
					</Col>
				</ModalFooter>
			</Modal>
		</>
	);
};

export default CampaignAudit;
